import { useCallback, useRef, useState } from "react";
import { Download, Loader2, Share2, BadgeCheck } from "lucide-react";
import { track } from "@/lib/analytics";

type Props = {
  title: string;
  slug: string;
  rank: string;
  xp: number;
  certifiedCount: number;
};

function cssVar(name: string, fallback: string) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines) break;
    } else {
      line = test;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1]!;
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 4) last = last.slice(0, -1);
    if (last !== lines[maxLines - 1]) lines[maxLines - 1] = `${last}…`;
  }
  return lines;
}

/**
 * Client-side, zero-cost reward: a branded 1080x1350 "Field Certificate"
 * rendered on canvas the moment a discovery is certified. Instantly
 * downloadable and shareable - the dopamine payoff a PDF never delivers.
 */
export function FieldCertificate({ title, slug, rank, xp, certifiedCount }: Props) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const render = useCallback(async () => {
    const W = 1080;
    const H = 1350;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const bg = cssVar("--background", "#0b0d12");
    const fg = cssVar("--foreground", "#f5f3ef");
    const primary = cssVar("--primary", "#e8833a");
    const card = cssVar("--card", "#15181f");

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Halo behind the seal
    const glow = ctx.createRadialGradient(W / 2, 470, 40, W / 2, 470, 520);
    glow.addColorStop(0, primary);
    glow.addColorStop(1, "transparent");
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 1;

    // Frame
    ctx.strokeStyle = primary;
    ctx.lineWidth = 6;
    ctx.strokeRect(48, 48, W - 96, H - 96);
    ctx.strokeStyle = card;
    ctx.lineWidth = 2;
    ctx.strokeRect(74, 74, W - 148, H - 148);

    const center = (text: string, y: number, font: string, color: string, spacing = 0) => {
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      if (!spacing) {
        ctx.fillText(text, W / 2, y);
        return;
      }
      const chars = [...text];
      const total = chars.reduce((s, c) => s + ctx.measureText(c).width + spacing, -spacing);
      let x = W / 2 - total / 2;
      ctx.textAlign = "left";
      for (const c of chars) {
        ctx.fillText(c, x, y);
        x += ctx.measureText(c).width + spacing;
      }
      ctx.textAlign = "center";
    };

    center("SMARTASAURUS", 168, "700 34px Inter, system-ui, sans-serif", primary, 10);
    center("FIELD CERTIFICATE", 214, "600 22px Inter, system-ui, sans-serif", fg, 8);

    // Seal
    ctx.beginPath();
    ctx.arc(W / 2, 470, 150, 0, Math.PI * 2);
    ctx.fillStyle = card;
    ctx.fill();
    ctx.lineWidth = 8;
    ctx.strokeStyle = primary;
    ctx.stroke();
    center(String(certifiedCount), 470, "800 120px Inter, system-ui, sans-serif", fg);
    center("CERTIFIED", 530, "700 20px Inter, system-ui, sans-serif", primary, 6);

    center("VERIFIED DISCOVERY", 700, "700 20px Inter, system-ui, sans-serif", primary, 7);

    ctx.font = "800 60px Inter, system-ui, sans-serif";
    const lines = wrap(ctx, title.toUpperCase(), W - 260, 4);
    lines.forEach((l, i) => center(l, 780 + i * 72, "800 60px Inter, system-ui, sans-serif", fg));

    // Rank strip
    const stripY = 1080;
    ctx.fillStyle = card;
    ctx.fillRect(130, stripY, W - 260, 130);
    ctx.fillStyle = primary;
    ctx.fillRect(130, stripY, 8, 130);
    center(rank.toUpperCase(), stripY + 55, "700 34px Inter, system-ui, sans-serif", fg, 4);
    center(`${xp} XP`, stripY + 100, "600 26px Inter, system-ui, sans-serif", primary, 3);

    center(
      new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
      1278,
      "500 22px Inter, system-ui, sans-serif",
      fg,
    );
    ctx.globalAlpha = 0.6;
    center("smartasaurus.com", 1226, "600 20px Inter, system-ui, sans-serif", primary, 3);
    ctx.globalAlpha = 1;

    const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, "image/png"));
    return blob;
  }, [title, rank, xp, certifiedCount]);

  const make = async () => {
    setBusy(true);
    try {
      const blob = await render();
      if (blob) {
        blobRef.current = blob;
        setPreview(URL.createObjectURL(blob));
        track("Certificate Generated", { slug });
      }
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!blobRef.current) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blobRef.current);
    a.download = `smartasaurus-certificate-${slug}.png`;
    a.click();
    track("Certificate Downloaded", { slug });
  };

  const share = async () => {
    if (!blobRef.current) return;
    const file = new File([blobRef.current], `smartasaurus-${slug}.png`, { type: "image/png" });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: `I certified “${title}” on Smartasaurus. ${rank} · ${xp} XP` });
        track("Certificate Shared", { slug });
        return;
      }
    } catch {
      /* cancelled */
    }
    download();
  };

  return (
    <div className="mt-5 rounded-2xl border border-primary/40 bg-card/60 p-4">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <BadgeCheck className="h-3.5 w-3.5" /> Your reward
      </div>
      <p className="mt-2 text-sm font-semibold text-foreground">
        Claim your Field Certificate - a shareable badge with your rank, XP and this discovery on it.
      </p>

      {!preview ? (
        <button
          type="button"
          onClick={make}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-primary bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
          {busy ? "Minting…" : "Claim my certificate"}
        </button>
      ) : (
        <div className="mt-3 animate-fade-in">
          <img
            src={preview}
            alt="Your Smartasaurus field certificate"
            className="mx-auto w-full max-w-[260px] rounded-xl border border-border"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 rounded-2xl border border-primary bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground"
            >
              <Share2 className="h-3.5 w-3.5" /> Share it
            </button>
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-2 rounded-2xl border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-muted-foreground"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
